import { Controller } from '@hotwired/stimulus';
import consumer from '../channels/consumer';
import { getCsrfToken } from '../lib/csrf';

const MIN_LOADER_MS = 3000;
const SUBSCRIBE_TIMEOUT_MS = 5000;
const BROADCAST_TIMEOUT_MS = 60000;
const ERROR_DISMISS_MS = 5000;
const USER_ERROR_STATUSES = [403, 429];

export default class extends Controller {
  static targets = [
    'button',
    'status',
    'error',
    'loadingTemplate',
    'errorTemplate',
  ];
  static values = {
    exportUrl: String,
    channel: String,
    filename: String,
  };

  async export() {
    this.startedAt = Date.now();
    this.#showLoading();

    try {
      await this.#subscribe();

      const response = await fetch(this.exportUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': getCsrfToken(),
          Accept: 'application/json',
        },
        credentials: 'same-origin',
      });

      if (response.ok) {
        this.#awaitBroadcast();
      } else {
        this.#fail(await this.#errorMessage(response));
      }
    } catch {
      this.#fail();
    }
  }

  disconnect() {
    this.#unsubscribe();
    this.#clearTimers();
    clearTimeout(this.errorTimeout);
  }

  #subscribe() {
    return new Promise((resolve, reject) => {
      this.subscribeTimeout = setTimeout(() => {
        reject(new Error('ActionCable subscription timeout'));
      }, SUBSCRIBE_TIMEOUT_MS);

      this.subscription = consumer.subscriptions.create(
        this.channelValue,
        {
          connected: () => {
            clearTimeout(this.subscribeTimeout);
            resolve();
          },
          received: (data) => this.#handleMessage(data),
        }
      );
    });
  }

  #awaitBroadcast() {
    this.broadcastTimeout = setTimeout(() => this.#fail(), BROADCAST_TIMEOUT_MS);
  }

  async #errorMessage(response) {
    if (!USER_ERROR_STATUSES.includes(response.status)) return undefined;

    try {
      const data = await response.json();
      return data.error;
    } catch {
      return undefined;
    }
  }

  #fail(message) {
    this.#unsubscribe();
    this.#clearTimers();
    if (!this.element.isConnected) return;

    this.#showError(message);
  }

  #unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  #clearTimers() {
    clearTimeout(this.subscribeTimeout);
    clearTimeout(this.broadcastTimeout);
    clearTimeout(this.loaderTimeout);
  }

  async #handleMessage(data) {
    clearTimeout(this.broadcastTimeout);

    const remaining = MIN_LOADER_MS - (Date.now() - this.startedAt);
    if (remaining > 0) {
      await new Promise((resolve) => {
        this.loaderTimeout = setTimeout(resolve, remaining);
      });
    }

    if (data.status === 'success') {
      this.#triggerDownload(data.csv);
    } else {
      this.#showError();
    }

    this.#unsubscribe();
  }

  #showLoading() {
    this.#clearError();
    this.buttonTarget.classList.add('d-none');
    this.statusTarget.classList.remove('d-none');
    this.statusTarget.replaceChildren();
    this.statusTarget.appendChild(
      this.loadingTemplateTarget.content.cloneNode(true)
    );
  }

  async #triggerDownload(base64Csv) {
    this.#resetUI();

    const response = await fetch(
      `data:text/csv;charset=utf-8;base64,${base64Csv}`
    );
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const date = new Date().toISOString().slice(0, 10);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.filenameValue}-${date}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  #resetUI() {
    this.buttonTarget.classList.remove('d-none');
    this.statusTarget.classList.add('d-none');
    this.statusTarget.replaceChildren();
  }

  #showError(message) {
    this.#resetUI();

    const node = this.errorTemplateTarget.content.cloneNode(true);
    if (message) {
      node.querySelector('[data-error-text]').textContent = message;
    }
    this.errorTarget.replaceChildren(node);
    clearTimeout(this.errorTimeout);
    this.errorTimeout = setTimeout(() => this.#clearError(), ERROR_DISMISS_MS);
  }

  #clearError() {
    clearTimeout(this.errorTimeout);
    this.errorTarget.replaceChildren();
  }
}
